import React, { useState, useEffect, useMemo } from "react";
import styles from "./PerfilMain.module.scss";
import { getPortfolio } from "../../../store/actions/registerActions";
import styled from "styled-components";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileEditModalNew from "../../UI/ProfileEditModal/ProfileEditModalNew";
import AccountSettingsModal from "../../UI/AccountSettingsModal/AccountSettingsModal.tsx";
import ProfilePostSuggestionModal from "../../UI/ProfilePostSuggestionModal/ProfilePostSuggestionModal.tsx";
import NavBar from "./NavBar/NavBar";
import UserInfo from "../PerfilMain/UserInfo/UserInfo";
import MisComentarios from "./MisComentarios/MisComentarios";
import MisProyectos from "./MisProyectos/MisProyectos";
import MisProductos from "./MisProductos/MisProductos";
import MisAccesos from "./MisAccesos/MisAccesos";
import MisCotizacionesDeMateriales from "./MisCotizacionesDeMateriales/MisCotizacionesDeMateriales";
import MisDocumentacionesObra from "./MisDocumentacionesObra/MisDocumentacionesObra.tsx";
import Resumen from "./Resumen/Resumen.tsx";
import { PackagePlus, Star } from "lucide-react";
import { openCalificationModal } from "../../../store/actions";
import Breadcrumbs from "../../UI/Breadcrumbs/Breadcrumbs.tsx";
import {
  getProfileTabFromQuery,
  buildProfileUrl,
  getProfilePathInfo,
  getUserDisplayName,
} from "../../../utils/navigationHelpers.ts";

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  padding-right: 2%;
  padding-left: 2%;
  max-width: 150rem;
  margin: auto;

  @media (max-width: 900px) {
    margin-top: 80px;
    padding-right: 2rem;
    padding-left: 2rem;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    margin-top: 80px;
    padding-right: 2rem;
    padding-left: 2rem;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;
  width: 100%;
  overflow: hidden;
  gap: 2rem;

  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
    padding-top: 0;
  }
`;

type ProfileTabName =
  | "Resumen"
  | "Productos/Servicios"
  | "Calificaciones"
  | "Proyectos publicados"
  | "Accesos a obras"
  | "Cotización de materiales"
  | "Documentaciones de obra";

interface UserProfileData {
  id?: string | number;
  type?: string;
  name?: string;
  last_name?: string;
  razon_social?: string;
  business_name?: string;
  [key: string]: unknown;
}

interface PerfilMainStateProps {
  perfilAjeno?: string | number;
  userData?: UserProfileData;
}

interface PerfilMainOwnProps {
  perfilAjeno?: string | number;
  profileVisualizerData?: UserProfileData | null;
  myPerson?: boolean;
}

interface PerfilMainDispatchProps {
  getPortfolio: (userId?: string | number) => void;
  openCalificationModal: (user: UserProfileData | null) => void;
}

type PerfilMainProps = PerfilMainStateProps &
  PerfilMainOwnProps &
  PerfilMainDispatchProps;

const PerfilMain = ({
  userData,
  myPerson,
  perfilAjeno,
  profileVisualizerData,
  openCalificationModal,
}: PerfilMainProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const profilePathInfo = useMemo(
    () => getProfilePathInfo(location.pathname),
    [location.pathname]
  );
  const [createNewPortfolio, setCreateNewPortfolio] = useState(false);

  const tabFromQuery = getProfileTabFromQuery(searchParams);
  const [isActive, setIsActive] = useState<ProfileTabName | string>(
    tabFromQuery || "Resumen"
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [, setInfoReady] = useState(false);

  useEffect(() => {
    const tab = getProfileTabFromQuery(searchParams);
    if (tab) {
      setIsActive(tab);
    }
  }, [location.search, searchParams]);

  useEffect(() => {
    setInfoReady(false);
  }, [isActive]);

  const handleTabChange = (tabName: string) => {
    setIsActive(tabName);
    const currentProfileUser = myPerson ? userData : profileVisualizerData;
    const fallbackUserId = myPerson ? userData?.id : perfilAjeno;
    const profileUserIdFromPath = profilePathInfo.userId;
    const profileUserNameFromPath = profilePathInfo.userName;

    const resolvedUserId =
      currentProfileUser?.id || fallbackUserId || profileUserIdFromPath;
    const resolvedDisplayName =
      getUserDisplayName(currentProfileUser) ||
      profileUserNameFromPath ||
      (myPerson ? getUserDisplayName(userData) : null) ||
      "perfil";

    if (resolvedUserId && resolvedDisplayName) {
      const baseUrl = buildProfileUrl(
        String(resolvedUserId),
        resolvedDisplayName,
        tabName
      );
      const preserveKeys = [
        "from",
        "skill",
        "jurisdictions",
        "allCountry",
        "page",
      ];
      const extraParams = new URLSearchParams();
      preserveKeys.forEach((key) => {
        const value = searchParams.get(key);
        if (value) {
          extraParams.set(key, value);
        }
      });
      const extraQuery = extraParams.toString();
      const newUrl = extraQuery
        ? `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${extraQuery}`
        : baseUrl;
      navigate(newUrl, { replace: true });
    }
  };

  const handleNewPortfolioClick = (value: boolean) => {
    setCreateNewPortfolio(value);
  };

  const renderSection = (): React.ReactNode => {
    switch (isActive) {
      case "Resumen":
        return (
          <Resumen
            myPerson={myPerson}
            userData={userData}
            profileVisualizerData={profileVisualizerData}
            perfilAjeno={perfilAjeno}
            setInfoReady={setInfoReady}
            onOpenPostModal={() => setIsPostModalOpen(true)}
          />
        );
      case "Productos/Servicios":
        return (
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionWrapper_header}>
              <h2 className={styles.sectionWrapper_header_title}>
                Productos y servicios
              </h2>
              {myPerson && (
                <button
                  className={styles.sectionWrapper_header_button}
                  onClick={() => handleNewPortfolioClick(true)}
                >
                  <PackagePlus size={20} />
                  Crear nuevo
                </button>
              )}
            </div>
            <MisProductos
              createNewPortfolio={createNewPortfolio}
              perfilAjeno={perfilAjeno}
              myPerson={myPerson}
              handleNewPortfolioClick={handleNewPortfolioClick}
              userData={userData}
              setInfoReady={setInfoReady}
              currentTab={isActive}
            />
          </div>
        );
      case "Calificaciones":
        return (
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionWrapper_header}>
              <h2 className={styles.sectionWrapper_header_title}>
                Calificaciones y reseñas
              </h2>
              {!myPerson && (
                <button
                  className={styles.sectionWrapper_header_button}
                  onClick={() =>
                    openCalificationModal(profileVisualizerData || null)
                  }
                >
                  <Star size={20} />
                  Calificar
                </button>
              )}
            </div>
            <MisComentarios
              perfilAjeno={perfilAjeno}
              myPerson={myPerson}
              setInfoReady={setInfoReady}
            />
          </div>
        );
      case "Proyectos publicados":
        return (
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionWrapper_header}>
              <h2 className={styles.sectionWrapper_header_title}>
                Proyectos publicados
              </h2>
            </div>
            <MisProyectos
              fromPage
              myPerson={myPerson}
              perfilAjeno={perfilAjeno}
              userData={userData}
              setInfoReady={setInfoReady}
              currentTab={isActive}
            />
          </div>
        );
      case "Accesos a obras":
        if (!myPerson) {
          return null;
        }
        return (
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionWrapper_header}>
              <h2 className={styles.sectionWrapper_header_title}>
                Accesos solicitados a obras
              </h2>
            </div>
            <MisAccesos
              myPerson={myPerson}
              setInfoReady={setInfoReady}
            />
          </div>
        );
      case "Cotización de materiales":
        return (
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionWrapper_header}>
              <h2 className={styles.sectionWrapper_header_title}>
                Solicitud de materiales
              </h2>
            </div>
            <MisCotizacionesDeMateriales
              myPerson={myPerson}
              setInfoReady={setInfoReady}
              profileVisualizerData={profileVisualizerData}
            />
          </div>
        );
      case "Documentaciones de obra":
        return (
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionWrapper_header}>
              <h2 className={styles.sectionWrapper_header_title}>
                Documentaciones de obra
              </h2>
            </div>
            <MisDocumentacionesObra
              myPerson={myPerson}
              userData={userData}
              profileVisualizerData={profileVisualizerData}
              setInfoReady={setInfoReady}
              currentTab={isActive}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <Wrapper>
        <div className={styles.breadcrumbContainer}>
          <Breadcrumbs />
        </div>
        <div className={styles.navBarContainer}>
          <UserInfo
            setIsEditing={setIsEditing}
            setIsSettingsOpen={setIsSettingsOpen}
            profileVisualizerData={profileVisualizerData}
            myPerson={myPerson}
          />
          <NavBar
            myPerson={myPerson}
            setIsActive={handleTabChange}
            setInfoReady={setInfoReady}
            isActive={isActive}
          />
        </div>

        <div className={styles.mainContainer}>
          <div className={styles.readyContainer}>{renderSection()}</div>
        </div>
        {isEditing && (
          <ProfileEditModalNew setIsEditing={setIsEditing} userData={userData} />
        )}
        {isSettingsOpen && (
          <AccountSettingsModal
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
            setInfoReady={setInfoReady}
          />
        )}
        {isPostModalOpen && (
          <ProfilePostSuggestionModal
            isOpen={isPostModalOpen}
            onClose={() => setIsPostModalOpen(false)}
            userData={userData}
          />
        )}
      </Wrapper>
    </Container>
  );
};

interface RootState {
  general?: {
    userData?: UserProfileData;
    [key: string]: unknown;
  };
}

const mapStateToProps = (
  state: RootState,
  ownProps: PerfilMainOwnProps
): PerfilMainStateProps => ({
  perfilAjeno: ownProps.perfilAjeno,
  userData: state.general?.userData,
});

export default connect(mapStateToProps, {
  getPortfolio,
  openCalificationModal,
})(PerfilMain);
